import { Show, useRecordContext, useRedirect, RaRecord } from 'react-admin';
import { Stack, Table, TableBody, Box, Typography, Chip } from '@mui/material';
import { SectionCard, Row, fmtCalendarDate, fmtDate } from '../../shared/Panel';
import {
  PersonCell,
  Vehicle,
  WorkSystemCell,
  WorkPermitCell,
  AvailableFromCell,
  availabilityOf,
} from './cells';
import { plNumber } from '../../shared/number';
import { ManualAdr } from '../fleet/ManualAdr';
import { EcoTruckScore } from './EcoTruckScore';
import { FeatureGate } from '../featureFlags/FeatureGate';

type DriverRecord = RaRecord & Record<string, unknown>;

const orDash = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '—' : String(v);

const Header = () => {
  const r = useRecordContext<DriverRecord>();
  const redirect = useRedirect();
  const cars = (r?.cars as DriverRecord[] | undefined) ?? [];
  if (!r) return null;
  return (
    <SectionCard title="Kierowca">
      <PersonCell record={r} />
      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        {r.active === true && <Chip size="small" color="success" label="aktywny" />}
        {r.active === false && <Chip size="small" color="warning" label="nieaktywny" />}
        <Box
          onClick={() =>
            cars[0] && redirect('show', 'cars', cars[0].id as number)
          }
          sx={{ cursor: cars[0] ? 'pointer' : 'default' }}
        >
          <Vehicle record={r} />
        </Box>
      </Box>
    </SectionCard>
  );
};

const ProfileCard = () => {
  const r = useRecordContext<DriverRecord>();
  if (!r) return null;
  return (
    <SectionCard title="Kartoteka">
      <Table size="small">
        <TableBody>
          <Row label="System pracy">
            <WorkSystemCell record={r} />
          </Row>
          <Row label="Preferowany język">{orDash(r.preferredLanguage)}</Row>
          <Row label="Numer karty kierowcy">{orDash(r.tachographCardNumber)}</Row>
          <Row label="Identyfikator w TMS">{orDash(r.tmsDriverId)}</Row>
          <Row label="Akronim w TMS">{orDash(r.tmsAcronym)}</Row>
          <Row label="Telefon">
            {/* Klient nie prowadzi telefonu kierowcy — takiego pola nie ma
                w jego strukturze (pytanie 7). Pusto tu jest stanem
                docelowym, dopóki tego nie zmieni. Na ekranie bez „klient”
                (poprawa brzmienia 01.10.2026): dla planisty źródłem jest TMS. */}
            {r.phone ? String(r.phone) : 'TMS nie przechowuje telefonu kierowcy'}
          </Row>
        </TableBody>
      </Table>
    </SectionCard>
  );
};

const QualificationsCard = () => {
  const r = useRecordContext<DriverRecord>();
  if (!r) return null;
  return (
    <SectionCard title="Uprawnienia i terminy">
      <Table size="small">
        <TableBody>
          <Row label="Zezwolenie na pracę do">
            <WorkPermitCell record={r} />
          </Row>
          <Row label="Uprawnienia na wózek do">
            {r.forkliftEligibilityUpTo
              ? fmtCalendarDate(r.forkliftEligibilityUpTo as string)
              : 'brak daty'}
          </Row>
          {/* ADR — wpis ręczny (07.10.2026): TMS go nie podaje. Za przełącznikiem
              `manualAdr` (12.10.2026) — wyłączony: wiersza nie ma, jak na produkcji. */}
          <FeatureGate feature="manualAdr">
            <Row label="ADR">
              <ManualAdr resource="drivers" />
            </Row>
          </FeatureGate>
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Terminy pokazujemy tak, jak zapisano je w kartotece TMS. O dopuszczeniu
        kierowcy do pracy decyduje dział kadr.
      </Typography>
    </SectionCard>
  );
};

/**
 * Czas pracy — oszacowanie z limitów 561/2006 (bez ulg) na podstawie czasów
 * jazdy z telematyki i nieobecności z kartoteki klienta. Składnik dla
 * planisty, nie werdykt: rozdział FTL mówi, że weryfikacja czasu pracy
 * „wymaga interpretacji".
 */
const DrivingTime = () => {
  const r = useRecordContext<DriverRecord>();
  if (!r) return null;
  const d = availabilityOf(r);
  const hours = (g: number | null) => (g === null ? '—' : `${plNumber(g)} h`);
  return (
    <SectionCard title="Czas pracy (szacunek)">
      <Table size="small">
        <TableBody>
          <Row label="Dostępność">
            <AvailableFromCell record={r} />
          </Row>
          {d && (
            <>
              <Row label="Pozostały czas jazdy (dziś / tydzień / 2 tygodnie)">
                {[d.remaining.todayH, d.remaining.weekH, d.remaining.twoWeeksH]
                  .map(hours)
                  .join(' / ')}
              </Row>
              <Row label="Czas jazdy do przerwy">{hours(d.remaining.untilBreakH)}</Row>
              <Row label="Najbliższa nieobecność">
                {d.nextAbsence
                  ? `${fmtCalendarDate(d.nextAbsence.od)} – ${fmtCalendarDate(d.nextAbsence.do)}${
                      d.nextAbsence.type ? ` (${d.nextAbsence.type})` : ''
                    }`
                  : 'brak w kartotece'}
              </Row>
            </>
          )}
          <FeatureGate feature="ecoTruckReliefs">
            <ReliefRows record={r} />
          </FeatureGate>
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Szacunek według limitów rozporządzenia 561/2006, bez ulg (założenie
        ostrożnościowe). Liczymy go z czasów jazdy z telematyki i nieobecności
        zapisanych w kartotece TMS.
        {d && d.zalozenia.length > 0 ? ` Założenia: ${d.zalozenia.join('; ')}.` : ''}{' '}
        Nie zastępuje sprawdzenia czasu pracy przez planistę.
      </Typography>
    </SectionCard>
  );
};

/** Liczniki ulg z eco-trucka — kształt `ecoTruckReliefCounters` z API. */
interface ReliefCounters {
  extendedDrivingDaysUsed: number | null;
  reducedDailyRestsCount: number | null;
  readAt: string | null;
}

const reliefCountersOf = (r: DriverRecord): ReliefCounters | null =>
  r.ecoTruckReliefCounters && typeof r.ecoTruckReliefCounters === 'object'
    ? (r.ecoTruckReliefCounters as ReliefCounters)
    : null;

/** „1 z 2” albo „brak danych” — brak licznika to nie zero. */
const usedOf = (used: number | null, max: number): string =>
  used === null ? 'brak danych' : `${used} z ${max}`;

/**
 * Ulgi kierowcy z eco-trucka (19.10.2026, decyzja 01.10.2026: gotowe
 * wartości z eco-trucka przed wyliczeniem). Surowe liczniki z telematyki
 * z chwilą odczytu — także starego (wtedy rekomendacje ich nie biorą).
 * Za przełącznikiem `ecoTruckReliefs`; bez wiersza telematyki — nic.
 */
const ReliefRows = ({ record }: { record: DriverRecord }) => {
  const c = reliefCountersOf(record);
  if (!c) return null;
  return (
    <>
      <Row label="Wydłużona jazda do 10 h w tygodniu (eco-truck)">
        {`wykorzystane ${usedOf(c.extendedDrivingDaysUsed, 2)}`}
      </Row>
      <Row label="Skrócone odpoczynki od odpoczynku tygodniowego (eco-truck)">
        {`wykorzystane ${usedOf(c.reducedDailyRestsCount, 3)}`}
      </Row>
      <Row label="Odczyt liczników ulg">{c.readAt ? fmtDate(c.readAt) : 'brak danych'}</Row>
    </>
  );
};

/**
 * Styl jazdy z eco-trucka (08.10.2026) — zwinięty, informacja pomocnicza.
 * Za przełącznikiem `ecoTruckDriverScore` (12.10.2026).
 */
const DrivingStyle = () => {
  const r = useRecordContext<DriverRecord>();
  if (!r) return null;
  return (
    <FeatureGate feature="ecoTruckDriverScore">
      <EcoTruckScore driverId={r.id} />
    </FeatureGate>
  );
};

export const DriverShow = () => (
  <Show title="Kierowca">
    <Stack spacing={2} sx={{ p: 2 }}>
      <Header />
      <ProfileCard />
      <DrivingTime />
      <QualificationsCard />
      <DrivingStyle />
    </Stack>
  </Show>
);
